import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch, useParams } from 'react-router';

import { Loader2 } from 'lucide-react';

import CommentsPanel from '@/components/comments/CommentsPanel';
import SectionTabs from '@/components/SectionTabs';
import { documentMachineConfig } from '@/config/documentStates';
import { templateSections } from '@/config/templateConfig';
import { DocumentContext } from '@/contexts/DocumentContext';
import useCognitoUsers from '@/hooks/useCognitoUsers';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';
import { useDocumentActions } from '@/hooks/useDocumentActions';
import { useDocumentData } from '@/hooks/useDocumentData';
import { useDocumentRevisions } from '@/hooks/useDocuments';
import { useTemplateById } from '@/hooks/useTemplatesAPI';
import PageLayout from '@/layouts/PageLayout';
import { getUserInfo } from '@/lib/auth';
import { apiService } from '@/lib/axios';
import { toApprovalsRows } from '@/lib/utils';
import {
  DocumentActionsHead,
  DocumentActionsTail,
} from '@/pages/documents/DocumentActions';

const DocumentDetailPage = () => {
  const { id, templateId } = useParams();
  const location = useLocation();

  const [form, setForm] = useState({ templateId });
  const [reviews, setReviews] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [periodReviewYears, setPeriodReviewYears] = useState(3);
  const [activeTab, setActiveTab] = useState('information');
  const matchNew = useMatch('/document/new');

  const { isSnapshotMode, isMasterPreview, mode } = useDetailMode(
    DETAIL_TYPE.DOCUMENT
  );

  const snapshotData = location.state?.snapshot || null;
  const snapshotAuditLogId = new URLSearchParams(location.search).get('snapshot') || null;
  const { document: displayDocument, isLoading: isDocumentLoading, snapshotComments } =
    useDocumentData({ id, isSnapshotMode, snapshotData, snapshotAuditLogId });

  // Document History & Approval: load all revisions (each with its linked
  // change controls) once the document is released, so the history table can
  // show one row per revision with real CC data.
  const isReleased = displayDocument?.status === 'RELEASED';
  const { revisions } = useDocumentRevisions(isReleased ? id : null);

  const { users: cognitoUsers = [] } = useCognitoUsers();
  const usersMap = useMemo(
    () => Object.fromEntries(cognitoUsers.map((u) => [u.sub, u])),
    [cognitoUsers]
  );

  const { allowedActions, rejectReason, setRejectReason, dialogs, actions } =
    useDocumentActions({
      documentId: id,
      documentData: displayDocument,
      form,
      reviews,
      periodReviewYears,
    });

  const reviewerRole = useMemo(() => {
    const reviewGroupOrder = documentMachineConfig.reviewGroupOrder || [];
    const reviewers = displayDocument?.reviewers;
    const currentActorIndex = displayDocument?.currentActorIndex ?? 0;

    if (!reviewers || !reviewGroupOrder.length) return null;

    const currentUserSub = getUserInfo()?.sub;

    if (!currentUserSub) return null;

    const pipeline =
      displayDocument?.reviewerPipeline ||
      reviewGroupOrder.flatMap((group) => reviewers[group] || []);

    if (!pipeline[currentActorIndex]) return null;

    const current = pipeline[currentActorIndex];

    if (
      currentUserSub !== (typeof current === 'object' ? current?.sub : current)
    )
      return null;

    let index = 0;

    for (const group of reviewGroupOrder) {
      const groupMembers = reviewers[group] || [];

      if (
        currentActorIndex >= index &&
        currentActorIndex < index + groupMembers.length
      ) {
        const roleMap = {
          processOwners: 'Process Owner',
          qmrQa: 'QA',
          managementApprovers: 'MGMT',
        };

        return roleMap[group] || group;
      }
      index += groupMembers.length;
    }
    return null;
  }, [
    displayDocument?.reviewers,
    displayDocument?.currentActorIndex,
    displayDocument?.reviewerPipeline,
  ]);

  const {
    load: loadTemplate,
    loading: isLoadingTemplate,
    template,
  } = useTemplateById();

  const isLoading = isDocumentLoading || isLoadingTemplate;

  const tabs = useMemo(
    () => [
      'information',
      'history',
      ...templateSections.content.items
        .filter(({ key }) => template?.config?.[key])
        .map(({ key }) => key),
      'approvals',
    ],
    [template]
  );

  const setFormByKey = useCallback((key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  useEffect(() => {
    if (mode === DETAIL_MODE.EDIT && displayDocument && !form.id) {
      setForm(displayDocument);
    }
  }, [displayDocument, mode, form.id]);

  useEffect(() => {
    if (!displayDocument || !!matchNew || !!form.id) return;

    const { qmrQa = [], processOwners = [], managementApprovers = [] } =
      displayDocument.reviewers ?? {};

    setForm((prev) => ({
      ...prev,
      approvalsAssigned: qmrQa?.length
        ? toApprovalsRows(qmrQa, usersMap)
        : null,
      approvalsOwner: processOwners?.length
        ? toApprovalsRows(processOwners, usersMap)
        : null,
      approvalsPersonnel: managementApprovers?.length
        ? toApprovalsRows(managementApprovers, usersMap)
        : null,
    }));
  }, [displayDocument, form.id, matchNew, usersMap]);

  useEffect(() => {
    if (mode === DETAIL_MODE.NEW) loadTemplate(templateId);
    else if (displayDocument?.templateId)
      loadTemplate(displayDocument.templateId);
  }, [displayDocument?.templateId, loadTemplate, mode, templateId]);

  // Snapshot mode uses routed comments; live documents load persisted revision comments.
  useEffect(() => {
    if (!id || !displayDocument?.revision || isSnapshotMode) return;

    apiService.comments
      .getAll({
        entityType: 'Document',
        entityId: id,
        entityRevision: displayDocument.revision,
      })
      .then((res) => {
        if (res.data.success) {
          const dbComments = res.data.data;
          const loadedReviews = dbComments.map((c) => ({
            id: c.id,
            tab: c.tab,
            text: c.text,
            updateAt: c.updatedAt,
            entityType: c.entityType,
            entityId: c.entityId,
            entityRevision: c.entityRevision,
            resolvedAt: c.resolvedAt,
            frozenAt: c.frozenAt,
            authorSub: c.authorSub,
            authorName: c.authorName,
          }));

          setReviews((prev) => {
            const savedIds = new Set(loadedReviews.map((r) => r.id));

            return [
              ...loadedReviews,
              ...prev.filter((r) => !savedIds.has(r.id)),
            ];
          });
        }
      })
      .catch(() => {});
  }, [id, displayDocument?.revision, isSnapshotMode]);

  const blockingCommentsCount = useMemo(() => {
    const currentSub = getUserInfo()?.sub;

    return reviews.filter((r) => !r.resolvedAt && r.authorSub === currentSub)
      .length;
  }, [reviews]);

  const shouldShowCommentsPanel =
    activeTab !== 'approvals' &&
    activeTab !== 'summary' &&
    (mode === DETAIL_MODE.STATIC ||
      (mode === DETAIL_MODE.EDIT &&
        displayDocument?.status === 'RETURNED')) &&
    !isMasterPreview &&
    (isSnapshotMode
      ? snapshotComments.some((c) => c.tab === activeTab)
      : !reviewerRole
        ? reviews.some((r) => r.tab === activeTab)
        : true);

  return (
    <DocumentContext.Provider
      value={{
        form,
        isPreview: mode === DETAIL_MODE.STATIC,
        mode,
        setFormByKey,
      }}
    >
      <PageLayout
        title={
          isSnapshotMode
            ? `Snapshot: ${displayDocument?.documentTitle || ''}`
            : mode === DETAIL_MODE.NEW
              ? 'New Document'
              : mode === DETAIL_MODE.EDIT &&
                  displayDocument?.status === 'RELEASED'
                ? `Periodic Review: ${displayDocument?.documentTitle || ''}`
                : `${displayDocument?.status === 'DRAFT' ? 'Draft' : 'Review'}: ${displayDocument?.documentTitle || ''}`
        }
        toolbarHead={
          <DocumentActionsHead
            actions={actions}
            documentStatus={displayDocument?.status}
          />
        }
        toolbarTail={
          <DocumentActionsTail
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabs={tabs}
            documentTitle={displayDocument?.documentTitle}
            canApprove={canApprove}
            reviews={reviews}
            actions={actions}
            allowedActions={allowedActions}
            documentStatus={displayDocument?.status}
            rejectReason={rejectReason}
            setRejectReason={setRejectReason}
            dialogs={dialogs}
            blockingCommentsCount={blockingCommentsCount}
          />
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading…</span>
          </div>
        ) : !template ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">No template selected</p>
          </div>
        ) : (
          <>
            {isSnapshotMode && (
              <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
                <strong>Historical Snapshot</strong> — This is a read-only view
                of the document at the time of this audit event. All editing and
                approval actions are disabled.
              </div>
            )}
            <SectionTabs
              activeTab={activeTab}
              detailType={DETAIL_TYPE.DOCUMENT}
              setActiveTab={setActiveTab}
              formData={mode === DETAIL_MODE.STATIC ? displayDocument : form}
              setFormData={setForm}
              template={template}
              reviews={reviews}
              reviewerRole={reviewerRole}
              docStatus={displayDocument?.status}
              periodReviewValue={periodReviewYears}
              revisions={revisions}
              setReviews={setReviews}
              setCanApprove={setCanApprove}
              onPeriodReviewChange={setPeriodReviewYears}
            />
            {shouldShowCommentsPanel && (
              <CommentsPanel
                className="mt-6"
                activeTab={activeTab}
                reviews={
                  isSnapshotMode
                    ? snapshotComments.filter((c) => c.tab === activeTab)
                    : reviews
                }
                reviewerRole={reviewerRole}
                periodReviewValue={periodReviewYears}
                setCanApprove={setCanApprove}
                onPeriodReviewChange={setPeriodReviewYears}
                setReviews={setReviews}
                entityId={id}
                entityRevision={displayDocument?.revision}
                currentUserSub={getUserInfo()?.sub}
                canSubmit={
                  isSnapshotMode
                    ? false
                    : reviewerRole && displayDocument?.status === 'REVIEWING'
                }
              />
            )}
          </>
        )}
      </PageLayout>
    </DocumentContext.Provider>
  );
};

export default DocumentDetailPage;
